import { ChevronRightIcon } from '@/assets/icons/icons';
import { Col, Flex, message, Row, Table, Typography } from 'antd';
import dayjs from 'dayjs';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { GetAirportsContacts } from '../../../apis/airports';
import { useToken } from '../../../common/helper';
import {
  CommonDrawer,
  CustomPagination,
  PageHeaderActions,
} from '../../../components';
import Breadcrumbs from '../../../components/Breadcrumbs';
import { useSpin } from '../../../components/SpinContext';
import useUnauthorize from '../../../hooks/useUnauthorize';
import ContactForm from './ContactForm';

const { Text } = Typography;

const NewCompanyContacts = () => {
  const location = useLocation();
  const { showSpin, hideSpin } = useSpin();
  const { token } = useToken();
  const { unauthorize } = useUnauthorize();
  const { t } = useTranslation('settings');

  const [search, setSearch] = useState('');
  const [pageSize] = useState(50);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [maxPageSize, setMaxPageSize] = useState(0);

  const handlePageChange = (newPage) => {
    const validPage = newPage > 0 ? newPage : 1;
    setPage(validPage);
  };

  const searchTimeoutRef = useRef(null);
  const handleSearchChange = (value) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      setSearch(value);
      setPage(1);
    }, 1000);
  };

  const [contactsList, setContactsList] = useState([]);
  const [selectedContact, setSelectedContact] = useState({});

  const [drawerOpen, setDrawerOpen] = useState(false);
  const mainMenu = useSelector((state) => state.menu.items);
  const airportAllPermissions = mainMenu?.find(
    (items) => items?.name.toLowerCase() === 'airports'
  );
  const airportPermissions = airportAllPermissions?.permissions;

  const airport = location.state?.airport;
  const airline = location.state?.airline;

  const breadcrumbItems = [
    { title: 'Airports', href: '/airport-list' },
    {
      title: airport?.name || 'Unknown Airport',
      href: `/airport-list/airport/${airport?.iata_code}`,
      state: { airport },
    },
    {
      title: airline?.name || 'Unknown Airline',
      href: `/airport-list/airport/airline-detail/${airline.name
        .replace(/\s+/g, '-')
        .toLowerCase()}`,
      state: { airport, airline },
    },
    { title: 'Company Contact' },
  ];

  // Fetch all contacts
  const GetAllContacts = useCallback(
    (pageCount, searchQuery) => {
      showSpin();
      const model = {
        airport: airport?.iata_code,
        airline: airline?.name,
        page: pageCount,
        search: searchQuery,
      };
      GetAirportsContacts(model, token)
        .then((res) => {
          const data = res?.data || null;
          if (data) {
            if (data.data?.is_success) {
              setContactsList(data?.data?.data);
              setTotalPages(data?.data?.page_count);
              setMaxPageSize(data?.data?.total_count);
            } else {
              message.error(data?.data?.message);
            }
          }
        })
        .catch((error) => {
          unauthorize(error);
        })
        .finally(() => {
          hideSpin();
        });
    },
    [airline?.name, airport?.iata_code, hideSpin, showSpin, token, unauthorize]
  );

  useEffect(() => {
    GetAllContacts(page, search);
  }, [page, search, GetAllContacts]);

  const refreshContacts = () => {
    GetAllContacts(page, search);
  };

  const totalCount = maxPageSize;
  const rangeStart = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = totalCount === 0 ? 0 : Math.min(page * pageSize, totalCount);
  // Helper function to convert timestamp to display format
  const formatTimeFromTimestamp = (timestamp) => {
    if (!timestamp) return '--';
    return dayjs(timestamp).format('h:mm A');
  };

  const handleEdit = (record) => {
    setSelectedContact(record);
    setDrawerOpen(true);
  };

  const handleAddContact = () => {
    setSelectedContact(null);
    setDrawerOpen(true);
  };

  const columns = [
    {
      title: 'Role',
      dataIndex: 'role',
      width: 100,
      ellipsis: true,
      render: (_, record) => (
        <Text ellipsis={{ tooltip: record?.role }} className='font-12'>
          {record?.role || '--'}
        </Text>
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      width: 100,
      ellipsis: true,
      render: (_, record) => (
        <Text ellipsis={{ tooltip: record?.name }} className='font-12'>
          {record?.name || '--'}
        </Text>
      ),
    },
    {
      title: 'Shift Hours',
      dataIndex: 'shiftHours',
      width: 100,
      ellipsis: true,
      render: (_, record) => {
        const val = `${formatTimeFromTimestamp(record?.start_time)} - ${formatTimeFromTimestamp(
          record?.end_time
        )}`;
        return (
          <Text ellipsis={{ tooltip: val }} className='font-12'>
            {val}
          </Text>
        );
      },
    },
    {
      title: 'Email',
      dataIndex: 'email',
      width: 100,
      ellipsis: true,
      render: (_, record) => (
        <Text ellipsis={{ tooltip: record?.email }} className='font-12'>
          {record?.email || '--'}
        </Text>
      ),
    },
    {
      title: 'Phone number',
      dataIndex: 'phone_number',
      width: 100,
      ellipsis: true,
      render: (_, record) => (
        <Text ellipsis={{ tooltip: record?.phone_number }} className='font-12'>
          {record?.phone_number || '--'}
        </Text>
      ),
    },
    {
      title: 'Notes',
      dataIndex: 'notes',
      width: 100,
      ellipsis: true,
      render: (_, record) => (
        <Text ellipsis={{ tooltip: record?.notes }} className='font-12'>
          {record?.notes || '--'}
        </Text>
      ),
    },
    {
      title: '',
      dataIndex: 'actions',
      width: 30,
      fixed: 'right',
      render: (_, record) => (
        <Flex align='center' justify='end'>
          {airportPermissions?.is_update && (
            <Flex
              justify='center'
              align='center'
              style={{ width: 32, height: 26, cursor: 'pointer' }}
              onClick={() => handleEdit(record)}
            >
              <ChevronRightIcon
                width='16'
                height='16'
                fill='var(--text-primary)'
              />
            </Flex>
          )}
        </Flex>
      ),
    },
  ];

  return (
    <>
      <section className='airline-list'>
        <Flex vertical gap={16}>
          <Breadcrumbs items={breadcrumbItems} />
          <PageHeaderActions
            showToggle={false}
            title={`Company contacts`}
            totalCount={maxPageSize}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
            addButtonText={t('add')}
            onSearchChange={handleSearchChange}
            onSearchClear={handleSearchChange}
            showAddButton={airportPermissions?.is_create}
            onAddClick={handleAddContact}
          />

          <div className='settings-content-wrapper'>
            <Table
              columns={columns}
              dataSource={contactsList}
              pagination={false}
              className='airline-contacts-table'
              rowHoverable={false}
              rowKey={(record) => record.id}
            />
          </div>
          {contactsList.length > 0 && (
            <Row className='w-full mt-3' justify='center'>
              <Col>
                <CustomPagination
                  page={page}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </Col>
            </Row>
          )}
        </Flex>
      </section>
      <CommonDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <ContactForm
          unauthorize={unauthorize}
          selelctedContact={selectedContact}
          drawerOpen={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          refreshList={refreshContacts}
          airportPermissions={airportPermissions}
          airport={airport}
          airline={airline}
        />
      </CommonDrawer>
    </>
  );
};

export default NewCompanyContacts;
